import styled from '@emotion/styled';
import Image from 'next/image';

import { CheckIcon } from './CheckIcon';
import { getProgressTheme, type ProgressType } from './progressBarUtils';

const LEFT_ICON_SIZE = 18;

const Container = styled.div<{ $color: string }>`
  height: 32px;
  width: 100%;
  border-radius: 20px;
  background-color: ${({ $color }) => $color};
  position: relative;
  overflow: hidden;
`;

const Progress = styled.div<{ $progress: number; $color: string; $delay: number }>`
  background-color: ${({ $color }) => $color};
  border-radius: 20px;
  position: absolute;
  inset: 0;
  right: ${({ $progress }) => Math.max(0, 100 - $progress)}%;
  transition: right 450ms ease-in-out;
  transition-delay: ${({ $delay }) => ($delay ?? 0) + 750}ms;
`;

const Content = styled.div`
  height: 100%;
  position: absolute;
  inset: 0;
  padding-left: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const LeftContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RightContent = styled.div`
  display: flex;
  align-items: center;
  padding-right: 40px;
`;

const RegularText = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #0f1629;
`;

const SemiBoldText = styled(RegularText)`
  font-size: 16px;
  font-weight: 500;
`;

const BoldText = styled(RegularText)`
  font-weight: 700;
`;

const IconContainer = styled.div`
  position: absolute;
  right: 6px;
  bottom: 0;
  top: 0;
  display: flex;
  align-items: center;
`;

type Props = {
  type?: ProgressType;
  progress: number;
  label: string;
  progressValue: number | string;
  goalValue: number | string;
  animationDelay?: number;
};

export const ProgressBar = ({
  label,
  progressValue,
  goalValue,
  progress = 0,
  type = 'neuroboost',
  animationDelay = 0,
}: Props) => {
  const { color, progessColor, iconSrc } = getProgressTheme(type);

  return (
    <Container $color={color}>
      <Progress $progress={progress} $color={progessColor} $delay={animationDelay} />
      <Content>
        <LeftContent>
          <Image src={iconSrc} alt="left icon" width={LEFT_ICON_SIZE} height={LEFT_ICON_SIZE} />
          <SemiBoldText>{label}</SemiBoldText>
          <BoldText>{progressValue}</BoldText>
        </LeftContent>
        <RightContent>
          <RegularText>Goal {goalValue}</RegularText>
          <IconContainer>
            <CheckIcon type={type} />
          </IconContainer>
        </RightContent>
      </Content>
    </Container>
  );
};
